import React from "react";
import RetreatListing from "../components/RetreatListing";
import FreeConsultationSection from "../components/FreeConsultationSection";
import Seo from "../components/Seo";
import { HERO_IMAGE_URL } from "../lib/heroImage";
import { Heart } from "lucide-react";
import { PRENATAL_YOGA } from "../lib/courseTypes";

const LOCKED_TYPE = PRENATAL_YOGA;

const faqItems = [
  {
    question: "What is prenatal yoga teacher training?",
    answer:
      "Prenatal yoga teacher training (often RPYT) prepares you to guide expecting mothers safely through pregnancy yoga. Programs cover trimester-specific asana, anatomy, breathing for labor, and teaching methodology.",
  },
  {
    question: "Where can I find prenatal yoga courses in India?",
    answer:
      "Rishikesh is a popular destination for prenatal and pregnancy yoga teacher training. Compare programs by duration, certification, accommodation, reviews and price on this page.",
  },
  {
    question: "Is prenatal yoga suitable during all trimesters?",
    answer:
      "Qualified prenatal programs adapt practice for each trimester. Always check each course description and consult your healthcare provider before enrolling.",
  },
];

function PrenatalYoga() {
  const title = "Prenatal Yoga Teacher Training in India";
  const description =
    "Compare prenatal and pregnancy yoga teacher training courses in Rishikesh and across India. Filter by duration, certification, rating and price to find RPYT programs for expecting mothers.";
  const canonicalPath = "/prenatal-yoga";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: title,
        url: `https://yogaretreatsbooking.com${canonicalPath}`,
        description,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.answer,
          },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pt-16">
      <Seo
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        ogImagePath="/favicon.ico"
        jsonLd={jsonLd}
      />

      <div className="relative h-[340px] bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE_URL}
            alt="Prenatal yoga teacher training in India"
            className="w-full h-full object-cover opacity-60"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-gray-900" />
        </div>

        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-widest mb-4 border border-blue-500/30 backdrop-blur-md">
              <Heart size={14} /> Pregnancy &amp; Prenatal Yoga
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
              Prenatal Yoga Teacher Training in India
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Compare prenatal and pregnancy yoga teacher training courses in Rishikesh and beyond. Find RPYT programs with certification, batches, reviews and prices in one place.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-10 relative z-10 pb-20">
        <FreeConsultationSection
          className="mb-8"
          context={{
            retreat_name: "Prenatal Yoga Courses",
            slug: "prenatal-yoga",
            location: "Online / India",
            askTeacher: true,
          }}
        />

        <RetreatListing
          lockedType={LOCKED_TYPE}
          heading="Prenatal Yoga Courses in India"
          subheading="Compare pregnancy yoga and RPYT programs by destination, duration, rating and price"
        />

        <section className="mt-12 bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 mb-4">FAQs about Prenatal Yoga in India</h2>
          <div className="space-y-4">
            {faqItems.map((item) => (
              <details key={item.question} className="group rounded-2xl border border-gray-100 p-4">
                <summary className="cursor-pointer font-bold text-gray-900">{item.question}</summary>
                <p className="mt-2 text-gray-600 text-sm leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default PrenatalYoga;
